import { z } from 'zod';
import { UserRole } from '../enums/roles';

/**
 * Login Request DTO Schema
 */
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Register Request DTO Schema
 */
export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
  name: z.string().min(1).max(255),
  companyName: z.string().min(1).max(255),
});

export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/**
 * Auth Token Response DTO Schema
 */
export const AuthTokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
});

export type AuthTokenResponse = z.infer<typeof AuthTokenResponseSchema>;

/**
 * Refresh Token Request DTO Schema
 */
export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
});

export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

/**
 * JWT Payload Schema
 *
 * MULTI-TENANCY HIERARCHY (Issue #170):
 * - tenantId: Top level - paying customer
 * - realmId: Middle level - isolated database workspace
 * - businessId: Bottom level - actual business entity
 *
 * All three MUST be present for proper multi-tenancy isolation.
 */
export const JWTPayloadSchema = z.object({
  sub: z.string().uuid(), // User ID
  email: z.string().email(),
  name: z.string().optional(), // Display name for portal greetings
  businessId: z.string().uuid(),
  tenantId: z.string().uuid(),
  realmId: z.string().uuid(),
  role: z.nativeEnum(UserRole),
  iat: z.number().int(),
  exp: z.number().int(),
});

export type JWTPayload = z.infer<typeof JWTPayloadSchema>;

/**
 * Impersonation Context Schema (Issue #321)
 *
 * Embedded in JWT during active impersonation session.
 * Used for audit attribution and session validation.
 */
export const ImpersonationContextSchema = z.object({
  /** Actor's user ID (the person doing the impersonating) */
  actorId: z.string().uuid(),

  /** Actor's email (for display and audit) */
  actorEmail: z.string().email(),

  /** Actor's original role */
  actorRole: z.nativeEnum(UserRole),

  /** Actor's business ID (for cross-business impersonation session lookup) */
  actorBusinessId: z.string().uuid().optional(),

  /** Mandatory reason for compliance */
  reason: z.string(),

  /** Unix timestamp when impersonation started */
  startedAt: z.number().int(),

  /** Unix timestamp when impersonation expires */
  expiresAt: z.number().int(),

  /** Session ID for audit correlation */
  sessionId: z.string().uuid(),
});

export type ImpersonationContext = z.infer<typeof ImpersonationContextSchema>;

/**
 * Extended JWT Payload with Impersonation (Issue #321)
 *
 * When impersonation context is present:
 * - sub, email, businessId, etc. are the TARGET user's values
 * - impersonation.actorId, actorEmail are the ACTOR's values
 * - All actions are attributed to the actor for audit compliance
 */
export const ImpersonationJWTPayloadSchema = JWTPayloadSchema.extend({
  impersonation: ImpersonationContextSchema.optional(),
});

export type ImpersonationJWTPayload = z.infer<typeof ImpersonationJWTPayloadSchema>;

/**
 * Authenticated User Schema
 *
 * Runtime representation of authenticated user attached to requests.
 * Extracted from validated JWT token by auth guards/strategies.
 *
 * MULTI-TENANCY FIELDS:
 * - id: User's unique identifier
 * - email: User's email address
 * - businessId: Bottom level - actual business entity
 * - tenantId: Top level - paying customer
 * - realmId: Middle level - isolated database workspace
 * - role: User's role (SUPER_ADMIN, BUSINESS_ADMIN, BUSINESS_USER, etc.)
 *
 * All three hierarchy fields (businessId, tenantId, realmId) MUST be present
 * for proper multi-tenancy isolation at all levels.
 */
export const AuthenticatedUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().optional(), // Display name for UI (comments, activity, etc.)
  businessId: z.string().uuid(),
  tenantId: z.string().uuid(),
  realmId: z.string().uuid(),
  role: z.nativeEnum(UserRole),
});

export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>;

/**
 * Authenticated User with Impersonation Context (Issue #321)
 *
 * Extended user object attached to requests during impersonation sessions.
 * The base fields (id, email, businessId, etc.) represent the TARGET user.
 * The impersonationContext contains the ACTOR's information for audit.
 */
export const AuthenticatedUserWithImpersonationSchema = AuthenticatedUserSchema.extend({
  /** Whether the current session is an impersonation session */
  isImpersonating: z.boolean().default(false),

  /** Impersonation context (only present when isImpersonating is true) */
  impersonationContext: ImpersonationContextSchema.optional(),
});

export type AuthenticatedUserWithImpersonation = z.infer<typeof AuthenticatedUserWithImpersonationSchema>;

/**
 * Password Reset Request DTO Schema
 */
export const PasswordResetRequestSchema = z.object({
  email: z.string().email(),
});

export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;

/**
 * Password Reset Confirm DTO Schema
 */
export const PasswordResetConfirmSchema = z.object({
  token: z.string(),
  newPassword: z.string().min(8).max(128),
});

export type PasswordResetConfirm = z.infer<typeof PasswordResetConfirmSchema>;

/**
 * Change Password DTO Schema
 */
export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(8).max(128),
  newPassword: z.string().min(8).max(128),
});

export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

/**
 * Enhanced Registration Request DTO Schema (with signup qualifiers)
 * @see GET /signup/config for available qualifier types
 */
export const EnhancedRegisterRequestSchema = z.object({
  // Account info
  email: z.string().email(),
  password: z.string().min(8).max(128),
  name: z.string().min(1).max(255),
  companyName: z.string().min(1).max(255),

  // Qualifier responses (dynamic structure based on qualifier types)
  // Example: { "industry": "technology", "company_size": "11-50", "use_case": ["solar-design", "energy-sim"] }
  qualifiers: z.record(
    z.string(),
    z.union([
      z.string().min(1).max(500), // single value
      z.array(z.string().min(1).max(100)).max(10), // multiple values
      z.number().int().min(0).max(1000000), // numeric value (e.g., company size)
    ]),
  ),

  // Terms acceptance
  tosAccepted: z.boolean().refine((val) => val === true, {
    message: 'You must accept the terms of service',
  }),
  tosVersion: z.string(),

  // Optional metadata
  referralSource: z.string().max(255).optional(),
  utmParams: z.record(z.string(), z.string()).optional(),
});

export type EnhancedRegisterRequest = z.infer<typeof EnhancedRegisterRequestSchema>;

/**
 * Signup Config Response DTO
 */
export const SignupConfigResponseSchema = z.object({
  signupEnabled: z.boolean(),
  approvalMode: z.enum(['auto', 'manual', 'disabled']),
  qualifierTypes: z.array(
    z.object({
      typeKey: z.string(),
      label: z.string(),
      description: z.string().optional(),
      inputType: z.enum(['select', 'multiselect', 'text', 'number']),
      isRequired: z.boolean(),
      displayOrder: z.number(),
      options: z.array(
        z.object({
          optionKey: z.string(),
          label: z.string(),
          description: z.string().optional(),
          icon: z.string().optional(),
        }),
      ),
    }),
  ),
  tosUrl: z.string().url(),
  tosVersion: z.string(),
  requireTosAcceptance: z.boolean(),
});

export type SignupConfigResponse = z.infer<typeof SignupConfigResponseSchema>;

/**
 * Enhanced Register Response DTO (auto-approved or pending)
 */
export const EnhancedRegisterResponseSchema = z.union([
  // Auto-approved response
  z.object({
    status: z.literal('auto_approved'),
    message: z.string(),
    user: z.object({
      id: z.string(),
      email: z.string().email(),
      name: z.string(),
      role: z.string(),
    }),
    business: z.object({
      id: z.string(),
      name: z.string(),
      subscriptionTier: z.string(),
      subscriptionStatus: z.string(),
    }),
    sandbox: z
      .object({
        sandboxId: z.string(),
        credentials: z.object({
          jwtToken: z.string(),
          apiKey: z.string(),
          webhookSecret: z.string(),
          loginUrl: z.string().url(),
          apiEndpoint: z.string().url(),
        }),
        expiresAt: z.string().datetime(),
        status: z.string(),
      })
      .optional(),
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int().positive(),
  }),
  // Pending approval response
  z.object({
    status: z.literal('pending_approval'),
    message: z.string(),
    signupAttemptId: z.string(),
    estimatedApprovalTime: z.string(),
  }),
]);

export type EnhancedRegisterResponse = z.infer<typeof EnhancedRegisterResponseSchema>;

/**
 * Signup Approval Request DTO
 */
export const SignupApprovalRequestSchema = z.object({
  approved: z.boolean(),
  rejectionReason: z.string().max(1000).optional(),
});

export type SignupApprovalRequest = z.infer<typeof SignupApprovalRequestSchema>;

/**
 * Sandbox Credentials Response DTO
 * Returned by Control Tower after successful sandbox provisioning
 */
export const SandboxCredentialsSchema = z.object({
  jwtToken: z.string(),
  apiKey: z.string(),
  webhookSecret: z.string(),
  loginUrl: z.string().url(),
  apiEndpoint: z.string().url(),
});

export type SandboxCredentials = z.infer<typeof SandboxCredentialsSchema>;

/**
 * Provision Sandbox Response DTO
 * Full response from Control Tower sandbox bootstrap
 */
export const ProvisionSandboxResponseSchema = z.object({
  sandboxId: z.string(),
  status: z.string(),
  expiresAt: z.string().datetime(),
  credentials: SandboxCredentialsSchema,
  logicalDatabaseCount: z.number().int(),
  resourceCount: z.number().int(),
});

export type ProvisionSandboxResponse = z.infer<typeof ProvisionSandboxResponseSchema>;
